import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiLogOut,
  FiArrowLeft,
  FiArrowRight,
  FiMessageCircle,
  FiClock,
  FiBookOpen,
} from 'react-icons/fi';
import Sidebar from '../core/Sidebar';
import '../core/exams/GMAT.css';
import Copilot from '../core/copilot/Copilot';
import {
  API_BASE_URL,
  getAuthHeaders,
  redirectToLoginWithReturn,
} from '../utils/utils';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getStoredUser } from '../utils/authSession';
import GetStartedWith from './GetStartedWith';
import ReadingComprehensionResults from './ReadingComprehensionResults';
import {
  buildLocalReadingReview,
  greSkillLabel,
  isReadingOptionSelected,
  parseStoredAnswer,
} from './readingComprehensionReview';

const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const matchMouseToRegions = (mouseMovements, regions) => {
  return mouseMovements.map((event) => {
    for (const [regionName, box] of Object.entries(regions)) {
      if (
        event.x >= box.x1 &&
        event.x <= box.x2 &&
        event.y >= box.y1 &&
        event.y <= box.y2
      ) {
        return { ...event, matchedRegion: regionName };
      }
    }
    return { ...event, matchedRegion: null };
  });
};

// Helper function to check if answer is correct for both single and multiple choice
const checkAnswerCorrectness = (questionData, userAnswer) => {
  const hasIndexKey = Array.isArray(questionData.correct_answers);
  const hasLetterKey = typeof questionData.correct_answer === 'string';
  if (!hasIndexKey && !hasLetterKey) {
    return false;
  }

  if (questionData.type === 'multiple_choice_multiple') {
    if (!Array.isArray(userAnswer) || !hasIndexKey) {
      return false;
    }

    const selectedIndices = userAnswer
      .map((item) => {
        if (typeof item === 'number') {
          return item;
        }
        if (typeof item === 'string' && /^-?\d+$/.test(item)) {
          return Number(item);
        }
        return questionData.options.indexOf(item);
      })
      .filter((index) => index >= 0)
      .sort((left, right) => left - right);
    const correctIndices = [...questionData.correct_answers].sort(
      (left, right) => left - right
    );

    return (
      selectedIndices.length === correctIndices.length &&
      selectedIndices.every((index, position) => index === correctIndices[position])
    );
  }

  let correctOptionIndex;
  if (hasLetterKey) {
    correctOptionIndex = questionData.correct_answer.charCodeAt(0) - 65;
  } else {
    correctOptionIndex = questionData.correct_answers[0];
  }

  if (typeof userAnswer === 'number') {
    return userAnswer === correctOptionIndex;
  }
  if (Array.isArray(userAnswer)) {
    return userAnswer.length === 1 && userAnswer[0] === correctOptionIndex;
  }
  return userAnswer === questionData.options[correctOptionIndex];
};

const RC_SECONDS_PER_QUESTION = 37.5;

const formatTimeFromMs = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${seconds} secs`;
  }
  if (seconds === 0) {
    return `${minutes} mins`;
  }
  return `${minutes} mins and ${seconds} secs`;
};

const computePerQuestionTimesMs = (questionStartTimes, endTime) =>
  questionStartTimes.map((start, index) => {
    const nextStart = questionStartTimes[index + 1];
    return (nextStart ?? endTime) - start;
  });

const computeStrategySignal = ({
  accuracyPercent,
  readingTimeMs,
  expectedReadingTimeMs,
  avgQuestionTimeMs,
}) => {
  const readingRatio =
    expectedReadingTimeMs > 0 ? readingTimeMs / expectedReadingTimeMs : 1;
  const questionTargetMs = RC_SECONDS_PER_QUESTION * 1000;

  if (accuracyPercent >= 80 && readingRatio >= 0.85) {
    return 'slow_but_effective';
  }
  if (accuracyPercent >= 80 && readingRatio < 0.6) {
    return 'fast_and_effective';
  }
  if (accuracyPercent < 60 && readingRatio < 0.6) {
    return 'too_fast_low_accuracy';
  }
  if (accuracyPercent < 60 && readingRatio >= 0.85) {
    return 'slow_low_accuracy';
  }
  if (avgQuestionTimeMs > questionTargetMs * 1.5 && accuracyPercent >= 70) {
    return 'accurate_but_slow_on_questions';
  }
  return 'balanced';
};

const RC_METHODOLOGY_PROMPT_FR = `**RÈGLES MÉTHODOLOGIQUES LIWAZA (OBLIGATOIRES):**
- Sur un passage long : consacre **3 à 5 minutes** à une lecture active (structure, intention de l'auteur, mots de contraste). Ne recommande **PAS** le skimming par défaut.
- Pour les questions : vise **30 à 45 secondes** par question une fois le passage bien compris.
- Si \`strategySignal\` vaut **"slow_but_effective"** (haute précision + lecture lente) : **VALIDE explicitement** la stratégie. Dis clairement que la compréhension prime sur la vitesse. Ne recommande **PAS** d'accélérer la lecture ni de skimmer.
- Si l'étudiant obtient de bons résultats avec une lecture lente ou à voix haute : **encourage** cette approche personnelle.
- Ne recommande d'accélérer la lecture que si la précision est faible **ET** la lecture était nettement trop rapide.
- Compare les temps réels aux valeurs \`expectedPassageReadingTime\` et \`expectedPerQuestionTime\` dans les données de performance.`;

export default function ReadingComprehensionTest({
  testData,
  loading,
  error,
  isReviewMode = false,
  existingResult = null,
  initialReview = null,
  passageId = null,
  onFetchReview = null,
  onSaveCompletion,
  onExit,
  spaceName = 'Verbal vault',
  examType = 'GRE', // 'GRE' or 'TAGE'
}) {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // All hooks must be called before any conditional returns
  const [timerStartedAt, setTimerStartedAt] = useState(() => Date.now());
  const [readingEndTime, setReadingEndTime] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questionStartTimes, setQuestionStartTimes] = useState([Date.now()]);
  const [questionTimers, setQuestionTimers] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [answerKeys, setAnswerKeys] = useState({});
  const [finished, setFinished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const mouseDataRef = useRef([]);
  const regionRefs = useRef({});
  const allUiRegionsRef = useRef({});
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [copilotVisible, setCopilotVisible] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingText, setGeneratingText] = useState(
    examType === 'TAGE'
      ? 'Analyse de ta performance en cours'
      : 'Analyzing your performance'
  );
  const [textFading, setTextFading] = useState(false);
  const [context, setContext] = useState('');
  const copilotMessagesEndRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [review, setReview] = useState(initialReview);
  const [showQuestionReview, setShowQuestionReview] = useState(false);
  const inQuestionReview = showQuestionReview;
  const resolvedPassageId = passageId || testData?.id || null;

  // A new passage must not inherit the previous clock, answers, or result.
  useEffect(() => {
    setTimerStartedAt(Date.now());
    setReadingEndTime(null);
    setQuestionIndex(0);
    setQuestionStartTimes([Date.now()]);
    setQuestionTimers([]);
    setTimeRemaining(0);
    setAnswers([]);
    setScore(0);
    setFinished(false);
    setIsSaving(false);
    setReview(null);
    setShowQuestionReview(false);
    setCopilotVisible(false);
    setCopilotMessages([]);
  }, [resolvedPassageId]);

  // Initialize questionTimers based on testData
  useEffect(() => {
    if (testData && questionTimers.length === 0) {
      setQuestionTimers(Array(testData.questions_data.length).fill(0));
      setTimeRemaining(testData.totalTimeMs);
    }
  }, [testData, questionTimers.length]);

  useEffect(() => {
    if (
      initialReview &&
      String(initialReview.passage_id) === String(resolvedPassageId)
    ) {
      setReview(initialReview);
    }
  }, [initialReview, resolvedPassageId]);

  // Load existing answers in review mode
  useEffect(() => {
    const reviewMatchesPassage =
      review?.questions?.length &&
      String(review.passage_id) === String(resolvedPassageId);
    if (reviewMatchesPassage) {
      const ordered = [...review.questions].sort(
        (left, right) => (left.order_in_passage || 0) - (right.order_in_passage || 0)
      );
      setAnswers(ordered.map((question) => parseStoredAnswer(question.user_answer)));
      setFinished(true);
      return;
    }

    if (isReviewMode && existingResult) {
      const userAnswers = existingResult.map((result) =>
        parseStoredAnswer(result.user_answer)
      );
      setAnswers(userAnswers);
      setFinished(true);
    }
  }, [isReviewMode, existingResult, review, resolvedPassageId]);

  // Countdown timer effect
  useEffect(() => {
    if (finished || isReviewMode || inQuestionReview) return undefined;

    const duration = Number(testData?.totalTimeMs);
    if (!Number.isFinite(duration) || duration <= 0) return undefined;

    const timer = setInterval(() => {
      const now = Date.now();
      const elapsed = now - timerStartedAt;
      const remaining = Math.max(0, duration - elapsed);

      setTimeRemaining(remaining);

      const questionElapsed = now - questionStartTimes[questionIndex];
      setQuestionTimers((prev) => {
        const updated = [...prev];
        updated[questionIndex] = questionElapsed;
        return updated;
      });

      if (remaining <= 0 && !finished) {
        clearInterval(timer);
        alert('Time is up! Your test will now be submitted.');
        finishTest();
      }
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    questionIndex,
    questionStartTimes,
    finished,
    timerStartedAt,
    testData?.totalTimeMs,
    isReviewMode,
    inQuestionReview,
  ]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const now = Date.now();
      const target = e.target.closest('.testprep-passage')
        ? 'passage'
        : e.target.closest('.testprep-question-box')
          ? 'question'
          : null;
      if (target) {
        mouseDataRef.current.push({
          x: e.clientX,
          y: e.clientY,
          time: now,
          area: target,
          questionIndex,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [questionIndex]);

  if (!testData) {
    return (
      <div className="layout">
        <Sidebar />
        <main className="content">
          <div className="layout-loading">
            <div className="loading-spinner"></div>
            <p>Loading reading comprehension test...</p>
          </div>
        </main>
      </div>
    );
  }

  // Split passage into paragraphs
  const passageParagraphs =
    testData.passage && testData.passage.trim()
      ? testData.passage.trim().split(/\n{2,}/)
      : ['No passage content available'];

  const captureCurrentRegions = () => {
    for (const [key, el] of Object.entries(regionRefs.current)) {
      if (el) {
        const rect = el.getBoundingClientRect();
        allUiRegionsRef.current[key] = {
          x1: rect.left,
          y1: rect.top,
          x2: rect.right,
          y2: rect.bottom,
          marker: key,
        };
      }
    }
  };

  const handleAnswer = (selectedOption, optionIndex) => {
    const updatedAnswers = [...answers];
    const storeIndices = examType === 'GRE';
    const token = storeIndices ? optionIndex : selectedOption;

    if (currentQuestion.type === 'multiple_choice_multiple') {
      if (!Array.isArray(updatedAnswers[questionIndex])) {
        updatedAnswers[questionIndex] = [];
      }

      const currentSelections = updatedAnswers[questionIndex];
      if (currentSelections.includes(token)) {
        updatedAnswers[questionIndex] = currentSelections.filter(
          (opt) => opt !== token
        );
      } else {
        updatedAnswers[questionIndex] = [...currentSelections, token];
      }
    } else {
      updatedAnswers[questionIndex] = storeIndices ? [optionIndex] : selectedOption;
    }

    setAnswers(updatedAnswers);
    if (questionIndex === 0 && !readingEndTime) setReadingEndTime(Date.now());
  };

  const handleNext = () => {
    captureCurrentRegions();
    const now = Date.now();
    if (questionIndex < testData.questions_data.length - 1) {
      setQuestionIndex((prev) => prev + 1);
      setQuestionStartTimes((prev) => [...prev, now]);
    } else {
      if (inQuestionReview) {
        setShowQuestionReview(false);
      } else if (isReviewMode) {
        onExit();
      } else {
        finishTest();
      }
    }
  };

  const handleBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex((prev) => prev - 1);
    }
  };

  const handleFileUpload = (e) => {
    const f = e.target.files[0];
    if (f) setSelectedFile(f);
  };

  const handleImageUpload = (e) => {
    const f = e.target.files[0];
    if (f) setSelectedImage(f);
  };

  // Helper function to generate with retry and model fallback
  const generateAIResponse = async (prompt) => {
    const storedUser =
      getStoredUser() || JSON.parse(sessionStorage.getItem('user') || 'null');
    const userEmail = storedUser?.email;

    if (!userEmail) {
      throw new Error('Not authenticated');
    }

    const userName =
      [storedUser?.first_name, storedUser?.last_name].filter(Boolean).join(' ') ||
      storedUser?.name ||
      '';
    const browserLanguage =
      typeof navigator !== 'undefined' ? navigator.language || '' : '';
    const language =
      examType === 'TAGE' || browserLanguage.toLowerCase().startsWith('fr')
        ? 'fr'
        : 'en';

    const response = await fetch(`${API_BASE_URL}/api/copilot-message`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        message: prompt,
        conversation_history: copilotMessages.map((message) => ({
          role: message.role === 'ai' ? 'assistant' : 'user',
          content: message.content,
        })),
        userEmail,
        user_context: {
          user_email: userEmail,
          user_name: userName,
          user_role: storedUser?.role?.name || storedUser?.role || 'student',
          current_page:
            typeof window !== 'undefined'
              ? window.location.pathname
              : '/reading-comprehension',
          priority_school: examType === 'TAGE' ? 'tage' : 'gre',
        },
        session_context: {
          component: 'ReadingComprehensionTest',
          exam_type: examType,
          passage_id: resolvedPassageId,
          language,
          test_title: testData?.title || null,
          test_topic: testData?.topic || null,
          test_difficulty: testData?.difficulty || null,
        },
        tools_available: ['test_prep_guidance'],
      }),
    });

    if (response.status === 401) {
      redirectToLoginWithReturn({
        navigate,
        message: 'Votre session a expire. Veuillez vous reconnecter.',
        publishLogoutReason: 'session_expired',
      });
      throw new Error('Not authenticated');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data?.metadata?.reading_comprehension_review) {
      setReview(data.metadata.reading_comprehension_review);
    }
    return (
      data.response ||
      'Unable to generate a response at the moment. Please try again in a few moments.'
    );
  };

  const handleSend = async () => {
    if (!context.trim()) return;
    setCopilotMessages((m) => [...m, { role: 'user', content: context }]);
    setContext('');
    setGenerating(true);

    try {
      const text = await generateAIResponse(context);
      setCopilotMessages((m) => [...m, { role: 'ai', content: text }]);
    } catch (err) {
      console.error('Failed to generate AI response:', err);
      setCopilotMessages((m) => [
        ...m,
        {
          role: 'ai',
          content:
            'Unable to generate a response at the moment. Please try again in a few moments.',
        },
      ]);
    }

    setGenerating(false);
  };

  const finishTest = async () => {
    setIsSaving(true);
    setCopilotVisible(true);
    setGenerating(true);
    captureCurrentRegions();
    const endTime = Date.now();
    const totalTime = endTime - timerStartedAt;
    const readingTime = readingEndTime ? readingEndTime - timerStartedAt : 0;
    const uiRegions = allUiRegionsRef.current;
    // eslint-disable-next-line no-unused-vars
    const matchedMouseData = matchMouseToRegions(
      mouseDataRef.current,
      uiRegions
    );

    let correct = 0;
    const discrepancies = [];

    // Calculate score
    for (let i = 0; i < answers.length; i++) {
      const isCorrect = checkAnswerCorrectness(
        testData.questions_data[i],
        answers[i]
      );

      if (isCorrect) {
        correct++;
      } else {
        const questionData = testData.questions_data[i];
        let correctAnswer;

        if (questionData.type === 'multiple_choice_multiple') {
          correctAnswer =
            questionData.correct_answers
              ?.map((index) => questionData.options[index])
              .join(', ') || 'N/A';
        } else {
          // Handle both TAGE format (correct_answer: "A") and GRE format (correct_answers: [0])
          let correctIndex;
          if (typeof questionData.correct_answer === 'string') {
            correctIndex = questionData.correct_answer.charCodeAt(0) - 65;
          } else if (Array.isArray(questionData.correct_answers)) {
            correctIndex = questionData.correct_answers[0];
          }
          correctAnswer = questionData.options[correctIndex] || 'N/A';
        }

        discrepancies.push({
          questionIndex: i + 1,
          studentAnswer: Array.isArray(answers[i])
            ? answers[i].join(', ')
            : answers[i],
          correctAnswer: correctAnswer,
          questionText: questionData.question,
          explanation: questionData.explanation,
        });
      }
    }

    // Save passage completion if callback provided
    let savedReview = null;
    if (onSaveCompletion) {
      try {
        savedReview = await onSaveCompletion({
          score: Math.round((correct / testData.questions_data.length) * 100),
          correctAnswers: correct,
          totalQuestions: testData.questions_data.length,
          timeSpent: Math.round(totalTime / 1000),
          readingTime: Math.round(readingTime / 1000),
          answers,
          questions: testData.questions_data,
        });
        if (savedReview?.correct_count != null) {
          correct = savedReview.correct_count;
        }
      } catch (error) {
        console.error('Error saving completion:', error);
      }
    }

    const questionAnsweringTime = readingEndTime
      ? totalTime - readingTime
      : totalTime;
    const totalQuestions = testData.questions_data.length;
    const accuracyPercent = Math.round((correct / totalQuestions) * 100);
    const perQuestionTimesMs = computePerQuestionTimesMs(
      questionStartTimes,
      endTime
    );
    const avgQuestionTimeMs =
      totalQuestions > 0 ? questionAnsweringTime / totalQuestions : 0;
    const expectedQuestionTimeMs =
      totalQuestions * RC_SECONDS_PER_QUESTION * 1000;
    const strategySignal = computeStrategySignal({
      accuracyPercent,
      readingTimeMs: readingTime,
      expectedReadingTimeMs: testData.expectedReadingTimeMs,
      avgQuestionTimeMs,
    });

    const simplifiedInsights = {
      testTitle: testData.title,
      testDifficulty: testData.difficulty,
      testTopic: testData.topic,
      totalQuestions,
      accuracyPercent,
      strategySignal,
      totalTime: formatTimeFromMs(totalTime),
      readingTime: formatTimeFromMs(readingTime),
      questionAnsweringTime: formatTimeFromMs(questionAnsweringTime),
      avgSecondsPerQuestion: Math.round(avgQuestionTimeMs / 1000),
      perQuestionTimesSeconds: perQuestionTimesMs.map((ms) =>
        Math.round(ms / 1000)
      ),
      studentScore: correct,
      passageLength: testData.passage.length,
      expectedTotalTime: formatTimeFromMs(testData.totalTimeMs),
      expectedPassageReadingTime: formatTimeFromMs(
        testData.expectedReadingTimeMs
      ),
      expectedPerQuestionTime: `${RC_SECONDS_PER_QUESTION} secs`,
      expectedQuestionTime: formatTimeFromMs(expectedQuestionTimeMs),
      answerDiscrepancies: discrepancies,
    };

    setScore(correct);
    setFinished(true);

    const localReview = buildLocalReadingReview({
      testData,
      answers,
      checkAnswerCorrectness,
    });
    if (localReview) {
      setReview(localReview);
    }
    if (savedReview?.correct_count != null) {
      setScore(savedReview.correct_count);
    }

    if (savedReview?.questions?.length) {
      setAnswerKeys(
        Object.fromEntries(
          savedReview.questions.map((question) => [
            question.question_id,
            {
              correct_answers: question.correct_answers,
              explanation: question.explanation,
            },
          ])
        )
      );
      setReview(savedReview);
    } else if (onFetchReview) {
      try {
        const remoteReview = await onFetchReview();
        if (remoteReview?.questions?.length) {
          setReview(remoteReview);
        }
      } catch (reviewError) {
        console.error('Error fetching GRE reading review:', reviewError);
      }
    }

    // Progressive loading messages with smooth transitions
    // Helper function to smoothly transition between messages
    const smoothTransition = (newText, delay) => {
      setTimeout(() => {
        setTextFading(true);
        setTimeout(() => {
          setGeneratingText(newText);
          setTextFading(false);
        }, 300); // Half of transition duration (matches CSS)
      }, delay);
    };

    if (examType === 'TAGE') {
      setGeneratingText('Analyse de ta performance en cours');
      smoothTransition('Comparaison avec les meilleurs scores', 2500);
      smoothTransition('Préparation de tes conseils personnalisés', 5500);
    } else {
      setGeneratingText('Analyzing your performance');
      smoothTransition('Comparing with top scores', 2500);
      smoothTransition('Preparing your personalized recommendations', 5500);
    }

    const greCoachingPrompt = `Analyze this GRE reading comprehension attempt. Score ${correct}/${totalQuestions} (${accuracyPercent}%). strategySignal=${strategySignal}. Explain missed questions by GRE skill (main idea, inference, vocab-in-context, sentence function, strengthen/weaken) and give one concrete next drill.`;

    const prompt =
      examType === 'TAGE'
        ? `Tu es un coach personnel TAGE MAGE spécialisé en CDT (Compréhension et Déduction Textuelle). Tu viens de regarder la performance de ton étudiant et tu vas lui parler directement, avec empathie et bienveillance.

## Détails du Test:
- Titre: ${testData.title}
- Sujet: ${testData.topic}
- Difficulté: ${testData.difficulty}
- Durée: ${testData.duration}

## Passage:
${testData.passage}

## Questions et Réponses Correctes:
${testData.questions_data
  .map((q, i) => {
    let correctAnswerText;
    if (q.type === 'multiple_choice_multiple') {
      correctAnswerText = q.correct_answers
        .map((idx) => q.options[idx])
        .join(', ');
    } else {
      // Handle both formats
      let correctIndex;
      if (typeof q.correct_answer === 'string') {
        correctIndex = q.correct_answer.charCodeAt(0) - 65;
      } else if (Array.isArray(q.correct_answers)) {
        correctIndex = q.correct_answers[0];
      }
      correctAnswerText = q.options[correctIndex];
    }
    return `Q${i + 1}: ${q.question}
  Réponse Correcte: ${correctAnswerText}
  Explication: ${q.explanation}`;
  })
  .join('\n\n')}

## Données de Performance de l'Étudiant:
${JSON.stringify(simplifiedInsights, null, 2)}

**TON ET STYLE DE COMMUNICATION:**
- Utilise systématiquement "tu" et "ton/ta" pour t'adresser à l'étudiant
- Commence par reconnaître ce qui a été bien fait avant de pointer les axes d'amélioration
- Utilise des phrases comme : "J'ai remarqué que tu...", "Tu as bien géré...", "Je te conseille de...", "Ton approche sur...", etc.
- Sois encourageant mais honnête - un bon coach ne cache pas la vérité mais la présente de façon constructive
- Utilise des exemples concrets tirés de sa performance
- Termine toujours sur une note positive et motivante

**STRUCTURE DE TON ANALYSE:**
1. **Ouverture personnalisée** - Salue l'étudiant et donne une vue d'ensemble de sa performance (2-3 phrases)
2. **Évaluation de la Gestion du Temps** - Analyse comment il a utilisé son temps
3. **Forces et Faiblesses Stratégiques** - Identifie ce qu'il fait bien ET ce qui doit être amélioré
4. **Analyse des Erreurs** - Explique pourquoi il s'est trompé sur certaines questions (avec bienveillance)
5. **Mes Conseils pour Toi** - 4 à 6 recommandations personnalisées et actionnables
6. **Conclusion Motivante** - Termine par un message d'encouragement personnel

**RÈGLES DE FORMATAGE STRICTES:**
- Utilise UNIQUEMENT des titres de niveau 2 (##) et 3 (###), JAMAIS de niveau 1 (#)
- Pour les données comparatives, utilise des **listes à puces** plutôt que des tableaux
- Format suggéré pour les comparaisons de temps:
  • Ton temps total : X mins (Temps recommandé : Y mins)
  • Ta lecture : X mins (Recommandé : Y mins)
  • Tes réponses : X mins (Recommandé : Y mins)
- Utilise des **paragraphes courts** (3-4 lignes maximum)
- Mets en **gras** les concepts clés et informations importantes
- Utilise des émojis de manière modérée pour rendre le texte plus visuel (✅ ⚠️ 💡 📊 🎯 etc.)

Note: Toutes les valeurs de temps dans les données de performance sont déjà formatées en "X mins et Y secs".

${RC_METHODOLOGY_PROMPT_FR}

**IMPORTANT: Tu dois répondre UNIQUEMENT en français. N'utilise JAMAIS l'anglais dans ta réponse. Parle DIRECTEMENT à l'étudiant avec "TU".**`
        : greCoachingPrompt;

    try {
      const text = await generateAIResponse(prompt);
      setCopilotMessages((prev) => [...prev, { role: 'ai', content: text }]);
    } catch (err) {
      console.error('Failed to generate AI insights after all retries:', err);
      const fallbackReview = localReview;
      const errorMessage =
        examType === 'TAGE'
          ? 'Impossible de générer les insights IA pour le moment. Tes résultats restent visibles à gauche : ouvre chaque question manquée pour voir la bonne réponse et l’explication.'
          : fallbackReview
            ? `I could not reach the live coach, but your GRE breakdown is ready on the results page: ${fallbackReview.correct_count}/${fallbackReview.total_questions}. Open each missed question to see why the correct choice is supported.`
            : 'Unable to generate AI insights at the moment. Your question-by-question review is still available on the results page.';

      setCopilotMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          content: errorMessage,
        },
      ]);
    }

    setGenerating(false);
    setIsSaving(false);
  };

  // Handle loading state
  if (loading || !testData) {
    return (
      <div className="layout">
        <Sidebar />
        <main className="content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '80vh',
              flexDirection: 'column',
              gap: '2rem',
              background: 'var(--liwaza-surface)',
              borderRadius: '12px',
              margin: '2rem',
              padding: '3rem',
            }}
          >
            <div className="layout-loading">
              <div className="loading-spinner"></div>
              <p>Loading reading comprehension test...</p>
            </div>

            <div
              style={{
                width: '200px',
                height: '4px',
                background: 'var(--liwaza-outline-variant)',
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: 'var(--liwaza-primary)',
                  borderRadius: '2px',
                  animation: 'loading-progress 2s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="layout">
        <Sidebar />
        <main className="content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh',
              flexDirection: 'column',
              gap: '2rem',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: '2rem',
                background: 'var(--liwaza-surface-container)',
                borderRadius: '12px',
                border: '1px solid var(--liwaza-outline-variant)',
                maxWidth: '500px',
              }}
            >
              <h1
                style={{
                  fontSize: '2rem',
                  color: 'var(--liwaza-on-surface)',
                  marginBottom: '1rem',
                }}
              >
                {error === 'Test not found'
                  ? 'Test not found'
                  : 'Content not available'}
              </h1>
              <p
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  marginBottom: '2rem',
                  lineHeight: 1.6,
                }}
              >
                {error === 'Test not found'
                  ? "The reading comprehension test you're looking for doesn't exist or may have been removed."
                  : getErrorMessage(error) ||
                    'This content is not available yet. Please contact support or try another lesson.'}
              </p>
              <button
                onClick={onExit}
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'white',
                  border: '2px solid var(--liwaza-primary)',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  margin: '0 auto',
                }}
              >
                <FiArrowLeft />
                Return to{' '}
                {examType === 'TAGE' ? 'TAGE MAGE' : 'Reading Comprehension'}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentQuestion = {
    ...testData.questions_data[questionIndex],
    ...(answerKeys[testData.questions_data[questionIndex]?.id] || {}),
  };

  // Safety check: if no questions or invalid question index
  if (!currentQuestion) {
    return (
      <div className="layout">
        <Sidebar />
        <main className="content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh',
              flexDirection: 'column',
              gap: '2rem',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: '2rem',
                background: 'var(--liwaza-surface-container)',
                borderRadius: '12px',
                border: '1px solid var(--liwaza-outline-variant)',
                maxWidth: '500px',
              }}
            >
              <h1
                style={{
                  fontSize: '2rem',
                  color: 'var(--liwaza-on-surface)',
                  marginBottom: '1rem',
                }}
              >
                No questions available
              </h1>
              <p
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  marginBottom: '2rem',
                  lineHeight: 1.6,
                }}
              >
                This test doesn't have any questions yet. Please contact support
                or try another test.
              </p>
              <button
                onClick={onExit}
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'white',
                  border: '2px solid var(--liwaza-primary)',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  margin: '0 auto',
                }}
              >
                <FiArrowLeft />
                Return to{' '}
                {examType === 'TAGE' ? 'TAGE MAGE' : 'Reading Comprehension'}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="layout">
      <Sidebar />
      <main className="content">
        <div className="testprep-header-bar">
          <div className="header-left">
            <h2>
              <FiBookOpen style={{ marginRight: '0.5rem' }} />
              {finished && !inQuestionReview
                ? testData.title
                : `${testData.title} - Question ${questionIndex + 1}`}
              {(isReviewMode || inQuestionReview) && (
                <span
                  style={{
                    marginLeft: '1rem',
                    fontSize: '0.8rem',
                    background: 'var(--liwaza-secondary-container)',
                    color: 'var(--liwaza-on-secondary-container)',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                  }}
                >
                  REVIEW MODE
                </span>
              )}
            </h2>
            {!isReviewMode && !finished && (
              <span className="test-timer">
                <FiClock style={{ marginRight: '0.25rem' }} />
                Time: {formatTime(timeRemaining)}
              </span>
            )}
          </div>
          <div className="header-right">
            <button className="nav-btn" onClick={onExit}>
              <FiLogOut size={20} />
              <span>Exit</span>
            </button>
            {(!finished || inQuestionReview) && (
              <>
                <button
                  className="nav-btn"
                  onClick={handleBack}
                  disabled={questionIndex === 0}
                >
                  <FiArrowLeft size={20} />
                  <span>Back</span>
                </button>
                <button
                  className="nav-btn"
                  onClick={handleNext}
                  disabled={(() => {
                    if (isSaving) return true;
                    if (finished || isReviewMode || inQuestionReview) return false;
                    if (!answers[questionIndex]) return true;

                    if (currentQuestion.type === 'multiple_choice_multiple') {
                      return (
                        !Array.isArray(answers[questionIndex]) ||
                        answers[questionIndex].length === 0
                      );
                    }

                    return !answers[questionIndex];
                  })()}
                >
                  <FiArrowRight size={20} />
                  <span>
                    {questionIndex === testData.questions_data.length - 1
                      ? isReviewMode || inQuestionReview
                        ? 'Back to results'
                        : 'Finish'
                      : 'Next'}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="testprep-container">
          <div className="testprep-passage">
            <h3
              style={{ marginBottom: '1rem', color: 'var(--liwaza-primary)' }}
            >
              {testData.topic} Passage
            </h3>
            {passageParagraphs.map((para, idx) => (
              <p
                key={idx}
                ref={(el) => (regionRefs.current[`passage_p${idx}`] = el)}
              >
                {para}
              </p>
            ))}
          </div>

          <div className="testprep-question-box">
            {!finished || inQuestionReview ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <span
                    style={{
                      background: 'var(--liwaza-primary-container)',
                      color: 'var(--liwaza-on-primary-container)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                    }}
                  >
                    Question {questionIndex + 1} of{' '}
                    {testData.questions_data.length}
                    {(isReviewMode || inQuestionReview) && (
                      <span
                        style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}
                      >
                        - Review Mode
                      </span>
                    )}
                  </span>
                </div>

                <p
                  className="question-text"
                  ref={(el) =>
                    (regionRefs.current[`question_${questionIndex}_text`] = el)
                  }
                >
                  {currentQuestion.question}
                </p>
                {examType === 'GRE' &&
                  (inQuestionReview ||
                    currentQuestion.gre_skill ||
                    currentQuestion.gre_skill_label) && (
                  <p
                    style={{
                      marginTop: '-0.35rem',
                      marginBottom: '1rem',
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    GRE skill:{' '}
                    {currentQuestion.gre_skill_label ||
                      greSkillLabel(
                        currentQuestion.gre_skill,
                        currentQuestion.type,
                        currentQuestion.question
                      )}
                  </p>
                )}

                {currentQuestion.type === 'multiple_choice_multiple' && (
                  <p
                    style={{
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.9rem',
                      fontStyle: 'italic',
                      marginBottom: '1rem',
                    }}
                  >
                    Select all that apply
                  </p>
                )}

                <div className="options-list">
                  {currentQuestion.options.map((opt, idx) => {
                    const id = `q${questionIndex}_opt${idx}`;

                    let isCorrectAnswer = false;
                    if (Array.isArray(currentQuestion.correct_answers)) {
                      if (currentQuestion.type === 'multiple_choice_multiple') {
                        isCorrectAnswer =
                          currentQuestion.correct_answers.includes(idx);
                      } else {
                        isCorrectAnswer =
                          idx === currentQuestion.correct_answers[0];
                      }
                    } else if (typeof currentQuestion.correct_answer === 'string') {
                      isCorrectAnswer =
                        idx ===
                        currentQuestion.correct_answer.charCodeAt(0) - 65;
                    }

                    const isSelectedAnswer = isReadingOptionSelected(
                      answers[questionIndex],
                      opt,
                      idx
                    );

                    const showReviewIndicators =
                      inQuestionReview &&
                      (Boolean(review?.questions?.length) ||
                        Boolean(existingResult) ||
                        finished);

                    let optionStyle = {};
                    if (showReviewIndicators) {
                      if (isCorrectAnswer) {
                        optionStyle = {
                          backgroundColor: 'var(--liwaza-success-container)',
                          border: '2px solid var(--liwaza-success)',
                          color: 'var(--liwaza-on-success-container)',
                        };
                      } else if (isSelectedAnswer && !isCorrectAnswer) {
                        optionStyle = {
                          backgroundColor: 'var(--liwaza-error-container)',
                          border: '2px solid var(--liwaza-error)',
                          color: 'var(--liwaza-on-error-container)',
                        };
                      }
                    }

                    return (
                      <div
                        key={id}
                        className={`option ${isSelectedAnswer ? 'selected' : ''}`}
                        ref={(el) => (regionRefs.current[id] = el)}
                        style={optionStyle}
                      >
                        <input
                          type={
                            currentQuestion.type === 'multiple_choice_multiple'
                              ? 'checkbox'
                              : 'radio'
                          }
                          id={id}
                          name={
                            currentQuestion.type === 'multiple_choice_multiple'
                              ? `question-${questionIndex}-${idx}`
                              : `question-${questionIndex}`
                          }
                          value={opt}
                          checked={isSelectedAnswer}
                          onChange={() =>
                            !isReviewMode &&
                            !inQuestionReview &&
                            handleAnswer(opt, idx)
                          }
                          disabled={isReviewMode || inQuestionReview}
                        />
                        <label
                          htmlFor={id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          {opt}
                          {showReviewIndicators && isCorrectAnswer && (
                            <span
                              style={{
                                fontSize: '0.8rem',
                                background: 'var(--liwaza-success)',
                                color: 'white',
                                padding: '0.2rem 0.4rem',
                                borderRadius: '4px',
                              }}
                            >
                              ✓ Correct
                            </span>
                          )}
                          {showReviewIndicators &&
                            isSelectedAnswer &&
                            !isCorrectAnswer && (
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  background: 'var(--liwaza-error)',
                                  color: 'white',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '4px',
                                }}
                              >
                                ✗ Your Answer
                              </span>
                            )}
                        </label>
                      </div>
                    );
                  })}
                </div>

                {inQuestionReview && (
                  <>
                    <div
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'var(--liwaza-surface-container-highest)',
                        borderRadius: '8px',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      <h4
                        style={{
                          color: 'var(--liwaza-primary)',
                          marginBottom: '0.5rem',
                        }}
                      >
                        Explanation:
                      </h4>
                      <p
                        style={{
                          color: 'var(--liwaza-on-surface)',
                          lineHeight: 1.6,
                          margin: 0,
                        }}
                      >
                        {currentQuestion.explanation}
                      </p>
                    </div>

                    {currentQuestion.type === 'multiple_choice_multiple' && (
                      <div
                        style={{
                          marginTop: '1rem',
                          padding: '1rem',
                          background: 'var(--liwaza-surface-container)',
                          borderRadius: '8px',
                          border: '1px solid var(--liwaza-outline-variant)',
                        }}
                      >
                        <h4
                          style={{
                            color: 'var(--liwaza-on-surface)',
                            marginBottom: '0.5rem',
                            fontSize: '0.9rem',
                          }}
                        >
                          Your Selected Answers:
                        </h4>
                        <p
                          style={{
                            color: 'var(--liwaza-on-surface-variant)',
                            margin: 0,
                            fontSize: '0.9rem',
                          }}
                        >
                          {Array.isArray(answers[questionIndex]) &&
                          answers[questionIndex].length > 0
                            ? answers[questionIndex].join(', ')
                            : 'No answers selected'}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <ReadingComprehensionResults
                review={
                  review ||
                  buildLocalReadingReview({
                    testData,
                    answers,
                    checkAnswerCorrectness,
                  })
                }
                score={score}
                totalQuestions={testData.questions_data.length}
                examType={examType}
                onReviewQuestions={() => {
                  setQuestionIndex(0);
                  setShowQuestionReview(true);
                }}
                onExit={onExit}
              >
                <div style={{ margin: '1.5rem 0 0' }}>
                  <GetStartedWith
                    title="Questions about your performance?"
                    spaceName={spaceName}
                    humanCoachTitle={`Talk to a ${examType === 'TAGE' ? 'TAGE MAGE' : 'GRE'} coach`}
                    communityTitle="Ask other students"
                    showHumanCoach={true}
                    showLiwazaAI={false}
                    showCommunity={true}
                  />
                </div>
              </ReadingComprehensionResults>
            )}
          </div>

          {copilotVisible ? (
            <Copilot
              messages={copilotMessages}
              generating={generating}
              generatingText={generatingText}
              textFading={textFading}
              context={context}
              setContext={setContext}
              handleSend={handleSend}
              handleFileUpload={handleFileUpload}
              handleImageUpload={handleImageUpload}
              selectedFile={selectedFile}
              selectedImage={selectedImage}
              messagesEndRef={copilotMessagesEndRef}
              onClose={() => setCopilotVisible(false)}
            />
          ) : (
            <div
              className="copilot-banner"
              onClick={() => setCopilotVisible(true)}
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                background: '#2D2F36',
                color: '#fff',
                padding: '10px 15px',
                borderRadius: '10px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 9999,
              }}
            >
              <FiMessageCircle style={{ marginRight: '8px' }} />
              Get insights from Liwaza AI
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
